import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, Fragment, useEffect } from 'react';
import { ShowHide } from "components/layout";
import moment from "moment";
import { Tooltip, Position, Icon } from "@blueprintjs/core";
import { DateToLocal } from "components/dates";

import './CalendarCell.css';

const defaultArray = [];

export function CalendarCell(
    {
        loading = false,
        cellIcons = [],
        displayDateFormat = "DD/MM/YYYY HH:mm",
        displayDate = true,
        datesAreUtc = true,
        periods = defaultArray,
        data,
        dayIndex,
        handleActivitySelectedChange,
        handleDaySelectedChange,
        isSelected,
        selectedActivity,
        showTimes,
        weekOdd
    }
) {
    const [shiftTimesJsx, setShiftTimesJsx] = useState(<></>);
    const [activityShiftsJsx, setActivityShiftsJsx] = useState(<></>);
    const [tooltipDisabled, setTooltipDisabled] = useState(true);
    const [shiftTimesTooltipJsx, setShiftTimesTooltipJsx] = useState(<></>);
    const assignedJourney = 4;

    function getTimeInfo(activityInfo, showEllipsis) {
        let ellipsis = showEllipsis ? <>&hellip;</> : <></>;
        return <Fragment key={activityInfo.shiftStartTime}><span className="calendar-cell-activity-square" style={{ backgroundColor: activityInfo.activityType.colour }} ></span><span className="calendar-cell-times">{moment(activityInfo.dayStartTimeBst).format('HH:mm')} - {moment(activityInfo.dayEndTimeBst).format('HH:mm')} </span>{ellipsis}</Fragment>;
    }

    // The vehicle calendar receives activity dates as UTC from the API; the manual entry template
    // builds them from the wall-clock times the operator typed, which must be shown as entered.
    function formatEntryDate(date) {
        if (datesAreUtc) {
            return <DateToLocal format={displayDateFormat}>{date}</DateToLocal>;
        }

        return <Fragment>{moment(date).format(displayDateFormat)}</Fragment>;
    }

    // Says where this day sits within an entry that may span several days, so neighbouring cells can
    // draw the entry as one joined band.
    function getSpanClasses(startDate, endDate) {
        const entryStart = moment(startDate);
        const entryEnd = moment(endDate);
        const dayStart = moment(data.date).startOf('day');
        const dayEnd = moment(data.date).endOf('day');

        return {
            'start': entryStart.isSameOrAfter(dayStart) && entryEnd.isAfter(dayEnd),
            'middle': entryStart.isBefore(dayStart) && entryEnd.isAfter(dayEnd),
            'end': entryStart.isBefore(dayStart) && entryEnd.isSameOrBefore(dayEnd),
            'single': entryStart.isSameOrAfter(dayStart) && entryEnd.isSameOrBefore(dayEnd),
        };
    }

    useEffect(() => {

        if (data && data.activityInfo) {
            if (data.activityInfo.length < 1) {
                setTooltipDisabled(true);
                setShiftTimesTooltipJsx(<></>);
                setShiftTimesJsx(<></>);
                setActivityShiftsJsx(<></>);
                return;
            };

            if (data.activityInfo.length > 2) {
                setTooltipDisabled(false);
                let jsx = data.activityInfo.map((a, index) => {
                    return (<span className="calendar-cell-tooltip" key={`calendar-cell-times-${dayIndex}-${index}`}>{getTimeInfo(a)}</span>);
                });
                setShiftTimesTooltipJsx(jsx);
                setShiftTimesJsx((<>{getTimeInfo(data.activityInfo[0], false)}{getTimeInfo(data.activityInfo[1], true)}</>));
            } else {
                let jlx = data.activityInfo.map((c) => {
                    return (getTimeInfo(c))
                });
                setTooltipDisabled(true);
                setShiftTimesTooltipJsx(<></>);
                setShiftTimesJsx(jlx)
            }

            let activityJsx = data.activityInfo.map((a, index) => {
                const currentActivityKey = `${a.shiftStartDate}-${a.shiftEndDate}`;
                const selectedActivityKey = selectedActivity
                    ? `${selectedActivity.shiftStartDate}-${selectedActivity.shiftEndDate}`
                    : null;

                const isSelectedValue = selectedActivityKey === currentActivityKey;

                const className = classNames('calendar-cell-activity-bar', {
                    'selected': isSelectedValue,
                    ...(isSelectedValue ? getSpanClasses(a.shiftStartDate, a.shiftEndDate) : {})
                });

                let additionalTooltip = '';

                if (a.activityType.id === assignedJourney && a.journeyName) {
                    additionalTooltip = (<span className="calendar-cell-tooltip">Journey type: {a.journeyName}</span>);
                } else if (a.driver?.driverFullName && a.driver?.driverFullName.trim().length > 0) {
                    additionalTooltip = (<span className="calendar-cell-tooltip">Driver: {a.driver?.driverFullName}</span>);
                }

                let activityTooltipJsx = (
                    <>
                        <span className="calendar-cell-tooltip">{a.activityType.name}</span>
                        {additionalTooltip}
                        <span className="calendar-cell-tooltip">Entry start: {formatEntryDate(a.shiftStartDate)}</span>
                        <span className="calendar-cell-tooltip">Entry end: {formatEntryDate(a.shiftEndDate)}</span>
                    </>
                );

                return (
                    <div
                        className="calendar-cell-activity-bar-container"
                        style={{ left: a.leftPercent, width: a.durationPercentage }}
                        key={`calendar-cell-activity-bar-${dayIndex}-${index}`}
                        onClick={(e) => handleActivitySelectedChange(e, a)}
                        onKeyDown={(e) => { }}
                        role="switch" tabIndex={0} aria-checked={false}
                    >
                        <Tooltip position={Position.TOP} content={activityTooltipJsx}>
                            <div
                                className={className}
                                style={{
                                    backgroundColor: a.activityType.colour
                                }}
                            >
                            </div>
                        </Tooltip>
                    </div>
                );
            });

            setActivityShiftsJsx(activityJsx);
        }
    }, [data, selectedActivity]);

    return (
        <Fragment>
            <div className={classNames({ "calendar-cell-selected": isSelected, "calendar-cell-week-odd": weekOdd }, 'calendar-cell', 'no-select')}
                onClick={(event) => handleDaySelectedChange(event, dayIndex)} onKeyDown={() => { }} role="switch" tabIndex={0} aria-checked={isSelected}>
                <div className="calendar-cell-info calendar-cell-info-top">
                    <span className={classNames({ "bp6-skeleton": loading })}>
                        {displayDate ? moment(data.date).format("DD MMM").toUpperCase() : ""}
                    </span>
                    <span className='calendar-cell-icons'>
                        {cellIcons.map(function (icon) {
                            return (
                                <ShowHide
                                    key={`icon-${dayIndex}-${icon.icon}`}
                                    evaluator={icon.evaluator}
                                    show={
                                        <div onClick={icon.clickEvent} onKeyDown={() => { }} role="switch" tabIndex={0} aria-checked={isSelected}>
                                            <Tooltip position={Position.TOP} content={icon.tooltipContent}>
                                                <Icon icon={icon.icon} className={classNames({ "cursor-pointer": icon.clickEvent })} style={{ color: icon.colour }} />
                                            </Tooltip>
                                        </div>
                                    }
                                >
                                </ShowHide>
                            );
                        })}
                    </span>
                </div>

                <div className="calendar-cell-activity">
                    {activityShiftsJsx }
                    <div className="calendar-cell-periods">
                        {periods.map((period) => (
                            <Tooltip key={period.key} position={Position.TOP} content={period.tooltip}>
                                <div className={classNames('calendar-cell-period-bar', getSpanClasses(period.startDate, period.endDate))}></div>
                            </Tooltip>
                        ))}
                    </div>
                </div>

                <ShowHide
                    evaluator={showTimes}
                    show={
                        <Tooltip position={Position.TOP} content={shiftTimesTooltipJsx} disabled={tooltipDisabled}>
                            <div className="calendar-cell-info calendar-cell-info-bottom">
                                {shiftTimesJsx}
                            </div>
                        </Tooltip>
                    }
                    hide={
                        <div className="calendar-cell-info calendar-cell-info-bottom"></div>
                    }
                ></ShowHide>

            </div>
        </Fragment>
    );
}

CalendarCell.propTypes = {
    data: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    isSelected: PropTypes.bool,
    dayIndex: PropTypes.number.isRequired,
    handleDaySelectedChange: PropTypes.func,
    weekOdd: PropTypes.bool,
    showTimes: PropTypes.bool,
    cellIcons: PropTypes.array,
    handleActivitySelectedChange: PropTypes.func,
    selectedActivity: PropTypes.object,
    displayDateFormat: PropTypes.string,
    displayDate: PropTypes.bool,
    datesAreUtc: PropTypes.bool,
    // Whole-day periods drawn in a thin strip below the activity bars. Each period gives its full
    // start and end so the cell can join it to the same period on neighbouring days.
    periods: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired,
        tooltip: PropTypes.node
    }))
};